import type { RootState } from "_shared/_stores/redux-store";
import type Guid from "_shared/guids/guid";
import { getCommentsByProposal } from "work/proposals/comments/_store/comments-redux-slice";
import type Comment from "work/proposals/comments/comment";
import type CommentThread from "work/proposals/comments/threads/comment-thread";

/**
 * How many comments `readerId` has not read in one of a proposal's two conversations: the server's
 * count, which takes in every comment loaded or not and is kept current by realtime and by every read
 * (#5828). The loaded ones until the server has sent a count.
 *
 * `isExternal` names the conversation as `CommentThread.isExternal` does. The pane's tab, the page's
 * Comments button and the traverser's conversation stops all count with this, so a stop is there exactly
 * while its tab shows a count (#5824).
 */
export function conversationUnreadCount(args: {
  commentThreads: readonly CommentThread[];
  isExternal: boolean;
  serverCounts: Readonly<Record<string, number>>;
  comments: readonly Comment[] | undefined;
  readerId: Guid | undefined;
}): number {
  const { commentThreads, isExternal, serverCounts, comments, readerId } = args;
  const threadId = commentThreads.find((thread) => thread.id && thread.isExternal === isExternal)?.id?.value;
  const serverCount = threadId ? serverCounts[threadId] : undefined;
  return (
    serverCount ??
    (comments ?? []).filter((comment) => comment.thread.isExternal === isExternal && comment.isUnreadBy(readerId))
      .length
  );
}

/** {@link conversationUnreadCount} from the store. A number, so a page selecting it renders only when it moves. */
export const getConversationUnreadCount =
  (args: { commentThreads: readonly CommentThread[]; proposalId: Guid | undefined; isExternal: boolean; readerId: Guid | undefined }) =>
  (state: RootState): number =>
    conversationUnreadCount({
      commentThreads: args.commentThreads,
      isExternal: args.isExternal,
      serverCounts: state.forumUnreadCounts.entries,
      comments: args.proposalId ? getCommentsByProposal(args.proposalId)(state) : undefined,
      readerId: args.readerId,
    });
